<template>
    <div class="field__radios" ref="radioGroup">
        <div v-for="answer in answers" class="field__radios__group" :key="answer.id">
            <input
                v-bind="{
                    ...$attrs,
                    id: `${$attrs.id}-${answer.label}`,
                    class: 'field__radios__input display--none',
                    value: answer.value
                }"
                @change="change( $event.target.value, $event )"
            >
            <base-label
                :settings="{
                    name: `${$attrs.id}-${answer.label}`,
                    class: 'field__radios__label accessibility--button pointer',
                    text: answer.label,
                }"
                tabindex="0"
                @keydown.native.enter.prevent="labelClick"
            />
        </div>
    </div>
</template>

<script>
// packages
import { ref } from '@vue/composition-api';

export default {
    inheritAttrs: false,
    components: {
        BaseLabel: () => import( /* webpackChunkName: 'IconAlert' */ '@/components/bundles/fields/blocks/Label' )
    },
    props: {
        answers: Array
    },
    setup ( _, { emit }) {
        const radioGroup = ref( null );

        /**
         * [method]
         * Listener de alteração no grupo de
         * radios buttons, no qual será emitido o valor
         * para o componente pai.
         *
         * @param {Number} value
         * @since 1.0.0
         * @category method
         * @author Tiago Martinho
         *
         */
        const change = ( value, $event ) => {
            // Aplicação do 'esbatido' nas outras opções.
            // 1 - definição das classes
            const jsActive = 'js--active';
            const jsInactive = 'js--inactive';

            // 2 - removido a class `js--active` caso exista
            const elementActive = radioGroup.value.querySelector( `.${jsActive}` );
            if ( elementActive )
                elementActive.classList.remove( jsActive );

            // 3 - obtenção do elemento pai da input selecionada & adicionado a class.
            const inputParent = $event.target.parentElement;
            inputParent.className = `field__radios__group ${jsActive}`;

            // 4 - adição as outras opções o 'esbatido'.
            const optionsWithoutActive = [ ...radioGroup.value.querySelectorAll( `.field__radios__group:not(.${jsActive}):not(.${jsInactive})` ) ];
            optionsWithoutActive.forEach( option => { option.classList.add( jsInactive ); });

            emit( 'input', parseInt( value, 10 ) );
        };

        /**
         * [method]
         * Adicionado o evento `keydown.enter` a label, de forma
         * a tornar a label acessível aquando a navegação pelo
         * site é feito através do teclado.
         *
         * @param {Object} $event
         * @since 1.0.0
         * @category method
         * @author Tiago Martinho
         *
         */
        const labelClick = $event => {
            // obtenção da input
            const input = $event.target.previousElementSibling;
            input.checked = true;
            // emissão do valor para componente pai.
            change( parseInt( input.value, 10 ), { target: input });
        };

        return { radioGroup, change, labelClick };
    }
};
</script>

<style lang="scss" scoped>
    // =============================================================================
    // Variables
    // =============================================================================
    $input-class: '.field__radios__input';
    $label-class: '.field__radios__label';

    // =============================================================================
    // Extends
    // =============================================================================
    %hover:hover {
        color: $color-green-light;
        border-color: $color-green-light;
    }

    // =============================================================================
    // Style
    // =============================================================================
    .field__radios {
        &__group {
            &.js--inactive {
                & #{$label-class} {
                    color: $color-grey;
                    border-color: $color-grey;
                    @extend %hover;
                }
            }
        }
        &__label {
            width: auto;
            @include typography-specs( 16px, 23px );
            color: $color-dark;
            padding: 0 10px;
            @include rounded-corner( 12px );
            border: 1px solid $color-dark;
            @include transition( all .2s ease-in-out );
            will-change: border-color, color;
            margin-left: 2px;
            @extend %hover;
            #{$input-class}:checked + & {
                color: $color-green;
                border-color: $color-green;
            }
        }
    }
</style>